import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { Box, Button, Typography } from "@mui/material";
import ChipComponent from "../chips/ChipComponent/ChipComponent";
import useInputValidation from "../../utils/useInputValidation";
import { getAllLabels, saveLabel } from "../../utils/api/label_apis";
import { STATUS } from "../../utils/api/sharedfunction";

// No more hardcoded COMMON_LABELS; will use API response

const MultiLabelInput = ({ value, onChange, placeholder, sx, onLabelCreated }) => {
  const [allLabels, setAllLabels] = useState([]);
  const [commonLabels, setCommonLabels] = useState([]); // For isDefault labels
  const [inputValue, setInputValue] = useState("");
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const { error, helperText, validateInput } = useInputValidation(20);

  // Fetch all labels for the selected project
  const fetchAllLabels = async () => {
    if (!selectedProject?.id) return [];
    try {
      const response = await getAllLabels();
      if (response.status === STATUS.SUCCESS) {
        const labels = response.data || [];
        setAllLabels(labels);
        // Set commonLabels to those with isDefault true
        setCommonLabels(labels.filter(l => l.isDefault));
        return labels;
      } else {
        console.error("Error fetching labels:", response.error);
        return [];
      }
    } catch (error) {
      console.error("Error fetching labels:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchAllLabels();
  }, [selectedProject?.id]);

  // Helper to get label object from allLabels by name (case-insensitive)
  const getLabelObj = (name) =>
    allLabels.find((l) => l.name.toLowerCase() === name.toLowerCase());

  // Helper to check if a label is already selected (case-insensitive)
  const isLabelSelected = (name) =>
    value.some((label) => label.name.toLowerCase() === name.toLowerCase());

  // Helper to check if a label exists in allLabels (case-insensitive)
  const isLabelExists = (name) =>
    allLabels.some((l) => l.name.toLowerCase() === name.toLowerCase());

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    validateInput(newInputValue);
  };

  // Add label (existing or new)
  const handleAddLabel = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || error) return;
    if (isLabelSelected(trimmed)) return;
    let updatedLabels = [...value];
    let labelObj = getLabelObj(trimmed);
    if (labelObj) {
      updatedLabels = [...updatedLabels, labelObj];
      onChange(updatedLabels);
      setInputValue("");
      return;
    }
    // Create label if it doesn't exist
    try {
      const response = await saveLabel({ labelName: trimmed });
      if (response.status === STATUS.SUCCESS) {
        const refreshed = await fetchAllLabels();
        labelObj = refreshed.find((l) => l.name.toLowerCase() === trimmed.toLowerCase());
        if (labelObj) {
          updatedLabels = [...updatedLabels, labelObj];
          onChange(updatedLabels);
        }
      } else {
        console.error("Error creating label:", response.error);
      }
    } catch (err) {
      console.error("Error creating label:", err);
    }
    setInputValue("");
  };

  // Remove label
  const handleRemoveLabel = (labelToRemove) => {
    onChange(value.filter((label) => label.id !== labelToRemove.id));
  };

  // Quick add common label (add if not selected)
  const handleQuickAdd = async (labelObj) => {
    if (isLabelSelected(labelObj.name)) return;
    onChange([...value, labelObj]);
  };

  // Autocomplete options: filter out already selected labels
  const filteredOptions = allLabels
    .filter((l) => !isLabelSelected(l.name))
    .map((l) => l.name);

  // Add button enabled if:
  // - input is non-empty and valid
  // - input does NOT match a selected label (case-insensitive)
  // (it can match an existing label in allLabels, as long as it's not already selected)
  const addButtonEnabled =
    !!inputValue.trim() &&
    !error &&
    !isLabelSelected(inputValue.trim());

  // Optional: Selecting from dropdown adds the label
  const handleAutocompleteChange = async (event, newValue, reason) => {
    if (reason === 'removeOption') {
      // Remove label
      const removed = value.filter(l => !newValue.includes(l.name));
      if (removed.length > 0) handleRemoveLabel(removed[0]);
      return;
    }
    // Add label (existing or new)
    const last = newValue[newValue.length - 1];
    if (!last) return;
    if (isLabelSelected(last)) return;
    let labelObj = getLabelObj(last);
    if (labelObj) {
      onChange([...value, labelObj]);
      setInputValue("");
      return;
    }
    // Create label if it doesn't exist
    try {
      const response = await saveLabel({ labelName: last });
      if (response.status === STATUS.SUCCESS) {
        const refreshed = await fetchAllLabels();
        labelObj = refreshed.find((l) => l.name.toLowerCase() === last.toLowerCase());
        if (labelObj) {
          onChange([...value, labelObj]);
          if (onLabelCreated) {
            onLabelCreated();
          }
        }
      } else {
        console.error("Error creating label:", response.error);
      }
    } catch (err) {
      console.error("Error creating label:", err);
    }
    setInputValue("");
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>

      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          options={filteredOptions}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          value={value.map(l => l.name)}
          onChange={handleAutocompleteChange}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const labelObj = getLabelObj(option);
              const { key, ...otherTagProps } = getTagProps({ index });
              return (
                <ChipComponent
                  key={labelObj?.id || option || index}
                  variant="label"
                  size="small"
                  removable={true}
                  onDelete={() => handleRemoveLabel(labelObj || { name: option })}
                  showTooltip={false}
                  {...otherTagProps}
                >
                  {option}
                </ChipComponent>
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={placeholder || 'Add a custom label...'}
              error={error}
              helperText={helperText}
              sx={{ flex: 1, borderRadius: 2 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          )}
          sx={{ flex: 1, borderRadius: 3 }}
        />
        <Button
          variant="contained"
          color="success"
          onClick={handleAddLabel}
          disabled={!addButtonEnabled}
          sx={{ minWidth: 80, height: 40, fontWeight: 500, fontSize: 14, bgcolor: '#43A047', color: 'white', borderRadius: 2, '&:hover': { bgcolor: '#4ade80' } }}
        >
          + Add
        </Button>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#334155', mb: 1 }}>
          Quickly assign common labels:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {commonLabels.map((labelObj) => (
            <ChipComponent
              key={labelObj.id}
              variant="label"
              size="small"
              clickable={true}
              onClick={() => handleQuickAdd(labelObj)}
              disabled={isLabelSelected(labelObj.name)}
              showTooltip={false}
            >
              + {labelObj.name}
            </ChipComponent>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MultiLabelInput;


